<template>
  <div>
    <div class="action-key-container">
      <label class="tb-label">
        Hot Keys
      </label>

      <p>
        Trigger by pressing buttons or using keyboard shortcuts
      </p>

      <div class="action-keys">
        <action-key
          v-for="(action, index) in actions"
          :key="index"
          :action="action"
          :production_context="production_context"
        />
      </div>
    </div>

    <div class="tb-form-group">
      <label class="tb-label">
        Video
      </label>

      <div v-if="src" class="media-upload-wrapper media-upload-wrapper--video">
        <video class="media" :src="src" :type="contentType" ref="videoPreview"></video>

        <div class="edit-media">
          <button
            class="tb-btn tb-btn-contained on-image"
            data-entity="producer-editor-editVideo"
            @click="selectAsset"
          >
            <span class="icon icon-pencil"></span>
          </button>
        </div>
      </div>

      <button
        v-else
        class="tb-btn tb-btn-contained medium-em"
        data-entity="producer-editor-selectVideo"
        @click="selectAsset"
      >
        <span class="icon icon-picture margin-right-xxs"></span>
        Choose Video
      </button>
    </div>

    <div class="tb-form-group">
      <div class="tb-checkbox" data-entity="producer-editor-toggleAutoplay">
        <input type="checkbox" id="autoplay" v-model="autoplay">
        <label class="checkbox" for="autoplay">Autoplay</label>
      </div>
    </div>

    <div class="tb-form-group">
      <div class="tb-checkbox" data-entity="producer-editor-toggleLoop">
        <input type="checkbox" id="loop" v-model="loop">
        <label class="checkbox" for="loop">Loop</label>
      </div>
    </div>

    <div class="tb-form-group">
      <div class="tb-checkbox" data-entity="producer-editor-toggleMute">
        <input type="checkbox" id="muted" v-model="muted">
        <label class="checkbox" for="muted">Start Muted</label>
      </div>
    </div>

    <div class="tb-form-group" role="group" aria-labelledby="in-out-label">
      <div id="in-out-label">In/Out</div>

      <div class="tb-range-slider">
        <input
          class="tb-range-slider__input"
          type="range"
          :min="0"
          :step="0.1"
          :max="Math.ceil(currentVideoDuration)"
          v-model.number="startTimeInSeconds"
          data-entity="producer-editor-startTimeInSeconds"
        />

        <input
          class="tb-range-slider__input"
          type="range"
          :min="0"
          :step="0.1"
          :max="Math.ceil(currentVideoDuration)"
          v-model.number="endTimeInSeconds"
          data-entity="producer-editor-endTimeInSeconds"
        />
      </div>
    </div>

    <div class="flex-row scrubber">
      <div class="input-group">
        <input
          v-model.lazy="startTimeFormatted"
          class="tb-input text-center"
          type="text"
          name="inTime"
          :class="{'is-invalid': errors.has('inTime') }"
          data-entity="producer-editor-startTimeFormatted"
        >

        <label class="tb-label">In</label>
      </div>

      <div class="input-group margin-left-xxs">
        <input
          v-model.lazy="endTimeFormatted"
          class="tb-input text-center"
          type="text"
          name="outTime"
          :class="{'is-invalid': errors.has('outTime') }"
          data-entity="producer-editor-endTimeFormatted"
        >

        <label class="tb-label">Out</label>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import ActionKey from '../../../panel-editor/hotkeys/ActionKey.vue';

dayjs.extend(duration);

export default {
  props: ['element', 'graphic_fields', 'production_context'],

  components: {
    ActionKey,
  },

  computed: {
    videoOptions() {
      return this.elementFields?.videoOptions ?? {};
    },

    actions() {
      return [
        {
          hotkey: 'P',
          display: 'Play/Pause',
          description: 'Use this hotkey to pause the video',
          value: 'play/pause',
          type: 'video',
          elementId: this.element.id,
        },
        {
          hotkey: 'M',
          display: 'Mute',
          description: 'Use this hotkey to mute the video',
          value: 'mute',
          type: 'video',
          elementId: this.element.id,
        },
      ];
    },

    currentVideoDuration() {
      return this.elementFields?.duration ?? this.element.duration;
    },

    currentStartTime() {
      return this.videoOptions.start ?? this.element.videoOptions.start;
    },

    currentEndTime() {
      return this.videoOptions.end ?? this.element.videoOptions.end;
    },

    elementFields() {
      return this.graphic_fields[this.element.id];
    },

    autoplay: {
      get() {
        return this.elementFields?.videoOptions?.autoplay ?? this.element.videoOptions.autoplay;
      },
      set(v) {
        this.updateField('videoOptions.autoplay', v);
      },
    },

    loop: {
      get() {
        return this.elementFields?.videoOptions?.loop ?? this.element.videoOptions.loop;
      },
      set(v) {
        this.updateField('videoOptions.loop', v);
      },
    },

    muted: {
      get() {
        return this.elementFields?.videoOptions?.muted ?? this.element.videoOptions.muted;
      },
      set(v) {
        this.updateField('videoOptions.muted', v);
      },
    },

    src() {
      return this.elementFields?.src ?? this.element.src;
    },

    contentType() {
      return this.elementFields?.contentType ?? this.element.contentType;
    },

    startTimeInSeconds: {
      get() {
        return this.currentStartTime;
      },

      set(v) {
        let newStart = v;

        if (v < 0) {
          newStart = 0;
        } else if (v >= this.currentVideoDuration) {
          newStart = this.currentVideoDuration;
          this.updateField('videoOptions.end', false);
        }

        if (v > this.endTimeInSeconds) {
          this.updateField('videoOptions.end', newStart);
        }

        this.updateField('videoOptions.start', newStart);
      },
    },

    startTimeFormatted: {
      get() {
        return dayjs.duration(this.startTimeInSeconds * 1000).format('m:ss');
      },
      set(v) {
        this.startTimeInSeconds = this.convertToSeconds(v);
      },
    },

    endTimeInSeconds: {
      get() {
        const endValue = this.currentEndTime;
        if (endValue === false) {
          return Math.ceil(this.currentVideoDuration);
        }

        return endValue;
      },

      set(v) {
        if (v >= this.currentVideoDuration) {
          this.updateField('videoOptions.end', false);
        } else {
          this.updateField('videoOptions.end', v);
        }

        if (v < this.startTimeInSeconds) {
          this.updateField('videoOptions.start', v);
        }
      },
    },

    endTimeFormatted: {
      get() {
        return dayjs.duration(this.endTimeInSeconds * 1000).format('m:ss');
      },
      set(v) {
        this.endTimeInSeconds = this.convertToSeconds(v);
      },
    },
  },
  watch: {
    startTimeInSeconds(time) {
      this.setTime(time);
    },
    endTimeInSeconds(time) {
      this.setTime(time);
    },
  },
  methods: {
    updateField(field, value) {
      this.$emit('value', { field, value });
    },

    setTime(t) {
      if (this.$refs.videoPreview) {
        this.$refs.videoPreview.currentTime = t;
      }
    },

    convertToSeconds(input) {
      const digitStr = input.replace(/\D/g, ''); // remove non digit chars
      const digitArr = digitStr.split('');

      return digitArr.reverse().reduce((total, d, idx) => {
        const numD = Number(d);

        switch (idx) {
          case 1: // 10s seconds
            return total + (numD * 10);
          case 2: // minutes
            return total + (numD * 60);
          case 3: // 10s minutes
            return total + (numD * 600);

            // TODO: support for longer than 1 hour?

          default:
            return total + numD;
        }
      }, 0);
    },

    selectAsset() {
      this.$store.dispatch('toggleAssetModal', { toggled: true, type: 'video' });

      window.assetmanfn = (asset) => {
        this.element.thumbnailUrl = asset.thumbnail_url; // eslint-disable-line

        this.updateField('thumbnailUrl', asset.thumbnail_url);
        this.updateField('src', asset.media_url);
        this.updateField('media_url', asset.media_url);
        this.updateField('duration', asset.meta.duration);
        this.updateField('contentType', asset.meta.content_type);
        this.updateField('videoOptions', {
          ...this.element.videoOptions,
          start: 0,
          end: false,
        });

        this.$store.dispatch('toggleAssetModal', { toggled: false, type: 'video' });
      };
    },
  },
};
</script>
